import jsPDF from 'jspdf';
import { SECTIONS_CONFIG } from '../Config/SectionsConfig';
import { A4_SHEET_FORMAT, DELAY_GENETATION_PER_SECTION, EXTENDED_REPORT_KEY, LANDSCAPE_ORIENTATION, MILLIMETERS_MEASUREMENT_UNIT } from '../Constants/ProjectReportConstants';
import { generatePageInPDF } from '../Helpers/CreatePDFHelper';
import { cleanupContainer, createOffscreenContainer } from '../Helpers/ContainerHelper';

interface generatePdfProjectReportProps {
    data: any;
    sections: any;
    isPMRType: boolean;
    onLoading: (loading: boolean) => void;
}

const generateSection = async (sectionId, data, pdf, container) => {
    const config = SECTIONS_CONFIG[sectionId];
    if (!config) return 0;

    let pagesRendered = 0;

    const renderPage = async (sectionData) => {
        await generatePageInPDF(
            sectionData,
            pdf,
            container,
            config.getComponent(sectionData),
            DELAY_GENETATION_PER_SECTION
        );
        pagesRendered++;
    };

    if (config.paginate) {
        const items = config.dataSource(data) || [];
        if (config.countKey) data[config.countKey] = items.length;

        if (items.length === 0) {
            data[config.dataKey] = [];
            await renderPage(data);
        } else {
            let chunkSize = config.isDynamicLimit ? Math.min(config.getDynamicLimit(items), config.limit) : config.limit;
            for (let i = 0; i < items.length; i += chunkSize) {

                if (sectionId === 'acquisitions' && i !== 0) {
                    data[config.fullScreenKey] = true;
                    chunkSize = config.limitFullScreen
                }

                data[config.dataKey] = items.slice(i, i + chunkSize);
                await renderPage(data);
            }
        }
    } else {
        await renderPage(data);
    }

    return pagesRendered;
};

export const generatePdfExtendedProjectReport = async ({ data, sections, isPMRType, onLoading }: generatePdfProjectReportProps) => {
    try {
        onLoading?.(true);

        const container = createOffscreenContainer();
        const pdf = new jsPDF(LANDSCAPE_ORIENTATION, MILLIMETERS_MEASUREMENT_UNIT, A4_SHEET_FORMAT);

        const productType = data.sectionOneGeneralInformation.projectProductType;
        const productClassification: string = data.sectionOneGeneralInformation.projectProductTypeClassification;

        if (productType === undefined || productClassification === undefined) {
            cleanupContainer(container);
            throw new Error("Missing required fields in data, aborting PDF generation.");
        }

        let pagesRendered = 0;
        data.typeReport = EXTENDED_REPORT_KEY;
        data.isPMRType = isPMRType;

        for (let section of sections) {
            pagesRendered += await generateSection(section.id, data, pdf, container);
        }

        if (pagesRendered === 0) {
            cleanupContainer(container);
            throw new Error("No valid pages were rendered. PDF generation aborted.");
        }

        pdf.deletePage(pdf.getNumberOfPages());
        pdf.save(`${EXTENDED_REPORT_KEY}-${data.sectionOneGeneralInformation.code}.pdf`);

        cleanupContainer(container);
    } catch (error) {
        throw new Error("Error generating PDF: " + error);
    } finally {
        onLoading?.(false);
    }
};

export { generateSection };
